import React from 'react';
import { ProductDetailsHeader } from '../product-details-header/product-details-header';
import { useProductDetailsPageV2 } from './hooks/use-product-details-page-v2';
import { LoadingSpinner, SVGS } from '@zonofi/zono-money-design-system';
import { ProductDetailsGalleryView } from '../product-details-gallery-view/product-details-gallery-view';
import './product-details-page-v2.scss';
import { onClickRaiseRequest } from '../utilities';
import { getFormattedAmount } from '@zonofi/common';
import { ProductDetailsImageGalleryV2 } from '../product-details-image-gallery-v2/product-details-image-gallery-v2';
import { WeightedPriceInfo } from '../weighted-price-info/weighted-price-info';
import { PricingStrategy } from '@zonofi/zono-money-store';

interface ProductDetailsPageV2Props {
  showOutOfStack: boolean;
}

export const ProductDetailsPageV2: React.FC<ProductDetailsPageV2Props> = ({
  showOutOfStack,
}) => {
  const {
    activeProductVariant,
    deleteVariantFromCart,
    getProductDetailsResponse,
    hasCartChanges,
    hideImages,
    hidePrice,
    isLoading,
    onAddToCart,
    onDecrementStagedVariant,
    onIncrementStagedVariant,
    onStagingTheVariant,
    orderId,
    productImages,
    productVariants,
    setShowImageGallery,
    showImageGallery,
    totalCartItems,
    waConfig,
    workspaceId,
    isEditing,
    editingVariant,
    openDescriptionDrawer,
    onOpenDescriptionDrawer,
    onCloseDescriptionDrawer,
    formatTitle,
  } = useProductDetailsPageV2();

  return (
    <>
      <div className="product-details-pagev2">
        {getProductDetailsResponse?.isLoading ? (
          <div className="product-details-loading">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <ProductDetailsHeader
              productName={activeProductVariant?.name}
              totalCartItems={totalCartItems}
              info={{ showIcon: true, onClick: onOpenDescriptionDrawer }}
            />
            <div className="product-details-pagev2-body">
              {!hideImages && (
                <div className="product-details-pagev2-body-images">
                  <ProductDetailsImageGalleryV2
                    images={productImages}
                    setShowImageGallery={setShowImageGallery}
                  />
                </div>
              )}
              <div className="product-details-pagev2-body-content">
                {/* <div className="content-description">
                  <span className="content-description-title font-primary-bold">
                    Description
                  </span>
                  <span className="content-description-text font-primary">
                    {activeProductVariant?.shortDescription ?? '-'}
                  </span>
                </div> */}
                <div className="product-variants">
                  <div className="product-variants-body">
                    {productVariants?.map((variant) => {
                      return (
                        <div className="product-variant-item">
                          <div className="product-variant-item-left">
                            <img
                              src={
                                variant?.itemInCart?.inCart ||
                                (showOutOfStack && variant?.stock < 1)
                                  ? SVGS.SquareCheckboxDisabled
                                  : variant?.itemInCart?.staging
                                    ? SVGS.SquareCheckboxBlack
                                    : SVGS.SquareCheckboxUnchecked
                              }
                              alt=""
                              className="checkbox-icon"
                              style={{
                                ...(showOutOfStack && variant?.stock < 1
                                  ? { cursor: 'default' }
                                  : {}),
                              }}
                              onClick={() => {
                                if (!(showOutOfStack && variant?.stock < 1)) {
                                  onStagingTheVariant(
                                    variant?.productVariantId
                                  );
                                }
                              }}
                            />
                            <div className="product-variant-item-left-text">
                              <span className="product-variant-item-left-text-name font-primary">
                                {formatTitle(variant?.title ?? '')}
                                {variant?.facets?.length
                                  ? `, ${variant.facets
                                      .map(
                                        (facet) =>
                                          `${facet.name}: ${facet.values
                                            ?.map((v) => v.facetValue)
                                            .join(', ')}`
                                      )
                                      .join(', ')}`
                                  : ''}
                              </span>
                              {!hidePrice && (
                                <span className="product-variant-item-left-text-price font-primary-semibold">
                                  {getFormattedAmount(variant?.price)}
                                  {variant?.details?.pricingStrategy ===
                                    PricingStrategy.WEIGHT_BASED && (
                                    <WeightedPriceInfo
                                      weightedPrice={
                                        variant?.details?.weightedPrice
                                      }
                                    />
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                          {showOutOfStack && variant?.stock < 1 ? (
                            <div className="catalog-list-out-of-stack-tag">
                              <span className="catalog-list-out-of-stack-tag-text font-primary">
                                OUT OF STOCK
                              </span>
                            </div>
                          ) : variant?.itemInCart?.inCart ||
                            variant?.itemInCart?.staging ? (
                            <div className="product-variant-item-right">
                              {variant?.itemInCart?.inCart &&
                                (isEditing &&
                                variant?.productVariantId ===
                                  editingVariant?.productVariantId ? (
                                  <LoadingSpinner size={14} />
                                ) : (
                                  <img
                                    src={SVGS.DeleteRed}
                                    alt=""
                                    onClick={() => {
                                      deleteVariantFromCart(variant);
                                    }}
                                    width={20}
                                    height={20}
                                    style={{
                                      padding: '5px',
                                    }}
                                  />
                                ))}
                              <div className="product-variant-item-right-button">
                                <img
                                  src={SVGS.MinuxIconBlue}
                                  alt=""
                                  onClick={() => {
                                    onDecrementStagedVariant(
                                      variant?.productVariantId
                                    );
                                  }}
                                />
                                <span className="product-variant-item-right-button-text font-primary-semibold">
                                  {variant?.itemInCart?.quantity}
                                </span>
                                <img
                                  src={SVGS.AddIconBlue}
                                  alt=""
                                  onClick={() => {
                                    onIncrementStagedVariant(
                                      variant?.productVariantId
                                    );
                                  }}
                                />
                              </div>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            <div className="product-details-pagev2-footer">
              <button
                className="product-details-footer-query-button"
                onClick={() => {
                  onClickRaiseRequest({
                    title: 'Need confirmation',
                    whatsappPhoneNo: waConfig?.whatsappPhoneNo,
                    workspaceId: workspaceId,
                  });
                }}
              >
                <img src={SVGS.WhatsAppIconWhite} alt="" />
                <div className="product-details-footer-query-button-text">
                  <span className="product-details-footer-query-button-text-small font-primary">
                    Any query?
                  </span>
                  <span className="product-details-footer-query-button-text-big font-primary-semibold font-size-medium">
                    Chat With Us
                  </span>
                </div>
              </button>
              <button
                className="product-details-footer-add-to-cart-button font-primary-semibold font-size-medium"
                onClick={() => onAddToCart()}
                disabled={!hasCartChanges}
              >
                <img src={SVGS.CartIconWhite} alt="" />
                {isLoading ? (
                  <LoadingSpinner size={15} />
                ) : orderId ? (
                  'Add To Order'
                ) : (
                  'Add To Cart'
                )}
              </button>
            </div>

            {openDescriptionDrawer && (
              <div
                className="product-details-pagev2-backdrop"
                onClick={onCloseDescriptionDrawer}
              />
            )}
            <div
              className={`product-details-pagev2-description ${
                openDescriptionDrawer ? 'open' : ''
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <span className="product-details-pagev2-description-heading font-primary-semibold">
                Description
              </span>
              <span className="product-details-pagev2-description-text font-primary-regular">
                {activeProductVariant?.shortDescription || 'No Description'}
              </span>
            </div>
          </>
        )}
      </div>
      {showImageGallery && (
        <ProductDetailsGalleryView
          open={showImageGallery}
          onClose={() => setShowImageGallery(false)}
          images={productImages.map((image) => {
            let newImage = image;
            if (image && image.startsWith('http')) {
              const urlObj = new URL(image ?? '');
              if (urlObj) {
                urlObj.searchParams.set('width', '500');
                urlObj.searchParams.delete('height', '500');
                newImage = urlObj.toString();
              }
            }
            return {
              src: newImage,
              alt: activeProductVariant?.name,
              type: 'image',
            };
          })}
        />
      )}
    </>
  );
};
